// ═══════════════════════════════════════════════════════════════
// Parcel Database — IndexedDB storage + address fuzzy matching
// Stores uploaded CSV parcel data locally; survives page refresh.
// ═══════════════════════════════════════════════════════════════

const DB_NAME    = 'ogemc_parcels';
const STORE_NAME = 'parcels';
const META_STORE = 'meta';
const DB_VERSION = 1;

export interface ParcelRecord {
  id:               number;   // auto-increment
  rawAddress:       string;
  city:             string;
  zip:              string;
  owner:            string;
  assessedValue:    number;
  normalizedKey:    string;   // for fast lookup
}

export interface ParcelMeta {
  count:      number;
  uploadedAt: string;
  fieldMap:   FieldMapping;
}

export interface FieldMapping {
  address:      string;
  city?:        string;
  assessedValue:string;
  owner?:       string;
}

// ── Address normalisation ──────────────────────────────────────

const ABBR: Record<string, string> = {
  st: 'street', ave: 'avenue', av: 'avenue', blvd: 'boulevard',
  dr: 'drive', rd: 'road', ln: 'lane', ct: 'court', pl: 'place',
  cir: 'circle', hwy: 'highway', pkwy: 'parkway', trl: 'trail',
  ter: 'terrace', ne: 'ne', nw: 'nw', se: 'se', sw: 'sw',
  n: 'n', s: 's', e: 'e', w: 'w',
};

export function normalizeAddress(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[.,#]/g, '')                    // strip punctuation
    .replace(/\b(apt|unit|ste|suite|#)\s*\w+/gi, '') // strip unit
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map(w => ABBR[w] ?? w)
    .join(' ');
}

// ── IndexedDB helpers ─────────────────────────────────────────

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
        store.createIndex('normalizedKey', 'normalizedKey', { unique: false });
      }
      if (!db.objectStoreNames.contains(META_STORE)) {
        db.createObjectStore(META_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
}

export async function saveParcels(
  rows: Omit<ParcelRecord, 'id'>[],
  meta: ParcelMeta,
): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction([STORE_NAME, META_STORE], 'readwrite');
    tx.objectStore(STORE_NAME).clear();
    rows.forEach(r => tx.objectStore(STORE_NAME).add(r));
    tx.objectStore(META_STORE).put(meta, 'meta');
    tx.oncomplete = () => resolve();
    tx.onerror    = () => reject(tx.error);
  });
}

export async function getParcelMeta(): Promise<ParcelMeta | null> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx  = db.transaction(META_STORE, 'readonly');
    const req = tx.objectStore(META_STORE).get('meta');
    req.onsuccess = () => resolve(req.result ?? null);
    req.onerror   = () => reject(req.error);
  });
}

export async function clearParcels(): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction([STORE_NAME, META_STORE], 'readwrite');
    tx.objectStore(STORE_NAME).clear();
    tx.objectStore(META_STORE).clear();
    tx.oncomplete = () => resolve();
    tx.onerror    = () => reject(tx.error);
  });
}

// ── In-memory lookup map (built once per session) ─────────────

let _map: Map<string, ParcelRecord> | null = null;

export async function getParcelMap(): Promise<Map<string, ParcelRecord>> {
  if (_map) return _map;
  const db = await openDb();
  const rows: ParcelRecord[] = await new Promise((resolve, reject) => {
    const tx  = db.transaction(STORE_NAME, 'readonly');
    const req = tx.objectStore(STORE_NAME).getAll();
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
  _map = new Map(rows.map(r => [r.normalizedKey, r]));
  return _map;
}

export function invalidateParcelMap(): void { _map = null; }

// ── Lookup ────────────────────────────────────────────────────

export interface ParcelMatch {
  parcel:     ParcelRecord;
  confidence: 'exact' | 'fuzzy';
}

export async function lookupParcel(address: string, city?: string): Promise<ParcelMatch | null> {
  const map = await getParcelMap();
  if (map.size === 0) return null;

  const needle = normalizeAddress(address);

  // 1. Exact match on address only
  const exact = map.get(needle);
  if (exact) return { parcel: exact, confidence: 'exact' };

  // 2. Try with city appended
  if (city) {
    const withCity = map.get(`${needle} ${city.toLowerCase().trim()}`);
    if (withCity) return { parcel: withCity, confidence: 'exact' };
  }

  // 3. Fuzzy — find entries whose key starts with the same street number + first street word
  const parts = needle.split(' ');
  if (parts.length >= 2) {
    const prefix = parts.slice(0, 2).join(' ');
    for (const [key, parcel] of map) {
      if (key.startsWith(prefix)) {
        return { parcel, confidence: 'fuzzy' };
      }
    }
  }

  return null;
}

// ── CSV parser ────────────────────────────────────────────────

export function parseCSVHeaders(csvText: string): string[] {
  const firstLine = csvText.split('\n')[0] ?? '';
  return firstLine.split(',').map(h => h.trim().replace(/^"|"$/g, ''));
}

export function parseAndStoreParcels(
  csvText: string,
  fieldMap: FieldMapping,
  onProgress?: (pct: number) => void,
): Promise<number> {
  return new Promise((resolve, reject) => {
    // Run in a microtask to not block UI
    setTimeout(async () => {
      try {
        const lines  = csvText.split('\n');
        const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));

        const idxAddr  = headers.indexOf(fieldMap.address);
        const idxVal   = headers.indexOf(fieldMap.assessedValue);
        const idxCity  = fieldMap.city  ? headers.indexOf(fieldMap.city)  : -1;
        const idxOwner = fieldMap.owner ? headers.indexOf(fieldMap.owner) : -1;

        if (idxAddr === -1 || idxVal === -1) throw new Error('Required columns not found');

        const rows: Omit<ParcelRecord, 'id'>[] = [];
        const total = lines.length - 1;

        for (let i = 1; i < lines.length; i++) {
          const line = lines[i].trim();
          if (!line) continue;

          // Simple CSV split (handles basic quoting)
          const cols = line.split(',').map(c => c.trim().replace(/^"|"$/g, ''));
          const rawAddr = cols[idxAddr] ?? '';
          const rawVal  = cols[idxVal]  ?? '';
          if (!rawAddr) continue;

          const assessedValue = parseFloat(rawVal.replace(/[$,]/g, '')) || 0;

          rows.push({
            rawAddress:    rawAddr,
            city:          idxCity  >= 0 ? (cols[idxCity]  ?? '') : '',
            zip:           '',
            owner:         idxOwner >= 0 ? (cols[idxOwner] ?? '') : '',
            assessedValue,
            normalizedKey: normalizeAddress(rawAddr),
          });

          if (i % 5000 === 0) onProgress?.(Math.round((i / total) * 90));
        }

        const meta: ParcelMeta = {
          count:      rows.length,
          uploadedAt: new Date().toISOString(),
          fieldMap,
        };
        await saveParcels(rows, meta);
        invalidateParcelMap();
        onProgress?.(100);
        resolve(rows.length);
      } catch (e) { reject(e); }
    }, 0);
  });
}
